import React, { useState, useRef, useCallback } from 'react';
import { Submission, Task, Participant } from '../../../types';
import { MediaDisplay, formatTimestamp } from '../../shared/media';
import { ConversationThread } from '../../thread/ConversationThread';
import './ResearcherFeedCard.css';

export interface ResearcherFeedCardProps {
  submission: Submission;
  participant?: Participant;
  task?: Task;
  unreadCount?: number;
  threadContext: {
    currentUserUid: string;
    currentUserName: string;
    organizationId: string;
  };
  onToggleStar?: (id: string) => void;
  onToggleReview?: (id: string) => void;
  onRequestRedo?: (id: string) => void;
}

export const ResearcherFeedCard: React.FC<ResearcherFeedCardProps> = ({
  submission,
  participant,
  task,
  unreadCount,
  threadContext,
  onToggleStar,
  onToggleReview,
  onRequestRedo,
}) => {
  const [showThread, setShowThread] = useState(false);
  const taskName = task?.title || task?.prompt || 'Task';
  const ts = submission.createdAt || submission.timestamp;
  const { relative } = formatTimestamp(ts);
  const isText = submission.mediaType === 'text';
  const hasUnread = (unreadCount ?? 0) > 0;

  /* ── Double-tap to toggle reviewed ── */
  const [showReviewFlash, setShowReviewFlash] = useState(false);
  const lastTapRef = useRef(0);

  const handleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      onToggleReview?.(submission.id);
      setShowReviewFlash(true);
      setTimeout(() => setShowReviewFlash(false), 800);
    }
    lastTapRef.current = now;
  }, [submission.id, onToggleReview]);

  /* ── Swipe actions ── */
  const [swipeX, setSwipeX] = useState(0);
  const [swipeFlash, setSwipeFlash] = useState<'star' | 'redo' | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeLocked = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    swipeLocked.current = false;
    setSwipeX(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;

    // Lock to horizontal if first movement is mostly horizontal
    if (!swipeLocked.current && Math.abs(dx) > 10) {
      if (Math.abs(dx) > Math.abs(dy) * 1.5) {
        swipeLocked.current = true;
      } else {
        touchStartRef.current = null;
        return;
      }
    }

    if (swipeLocked.current) {
      // Dampen the swipe — cap at ±120px
      const clamped = Math.max(-120, Math.min(120, dx));
      setSwipeX(clamped);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !swipeLocked.current) {
      touchStartRef.current = null;
      setSwipeX(0);
      return;
    }

    const THRESHOLD = 80;
    if (swipeX > THRESHOLD) {
      // Swipe right → star
      onToggleStar?.(submission.id);
      setSwipeFlash('star');
      setTimeout(() => setSwipeFlash(null), 600);
    } else if (swipeX < -THRESHOLD) {
      // Swipe left → redo request
      onRequestRedo?.(submission.id);
      setSwipeFlash('redo');
      setTimeout(() => setSwipeFlash(null), 600);
    }

    touchStartRef.current = null;
    swipeLocked.current = false;
    setSwipeX(0);
  }, [swipeX, submission.id, onToggleStar, onRequestRedo]);

  return (
    <div
      id={`feed-sub-${submission.id}`}
      className="rfc-card"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Swipe reveal indicators */}
      {swipeX !== 0 && (
        <div className={`rfc-swipe-bg ${swipeX > 0 ? 'rfc-swipe-bg--star' : 'rfc-swipe-bg--redo'}`}>
          {swipeX > 40 && (
            <svg className="rfc-swipe-icon rfc-swipe-icon--left" width="24" height="24" viewBox="0 0 24 24" fill="#C2AC8E" stroke="#C2AC8E" strokeWidth="2">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
          )}
          {swipeX < -40 && (
            <svg className="rfc-swipe-icon rfc-swipe-icon--right" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4715A" strokeWidth="2" strokeLinecap="round">
              <polyline points="1,4 1,10 7,10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          )}
        </div>
      )}

      {/* Card body — slides with swipe */}
      <div
        className="rfc-card-inner"
        style={swipeX !== 0 ? { transform: `translateX(${swipeX}px)`, transition: 'none' } : undefined}
      >
        {/* Swipe flash overlay */}
        {swipeFlash && (
          <div className={`rfc-swipe-flash rfc-swipe-flash--${swipeFlash}`}>
            {swipeFlash === 'star' ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#C2AC8E" stroke="#C2AC8E" strokeWidth="1.5">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D4715A" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="1,4 1,10 7,10" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
            )}
          </div>
        )}

        {/* ═══ Attribution header ═══ */}
        <div className="rfc-header">
          <div className="rfc-avatar">
            {participant?.avatarUrl ? (
              <img src={participant.avatarUrl} alt="" className="rfc-avatar-img" />
            ) : (
              <span className="rfc-avatar-initial">
                {(participant?.name || 'P').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="rfc-header-text">
            <span className="rfc-name">{participant?.name || 'Participant'}</span>
            <span className="rfc-task">{taskName}</span>
          </div>
          <span className="rfc-time">{relative}</span>
        </div>

        {/* ═══ Media — double-tap to review ═══ */}
        <div className="rfc-media-wrap" onClick={handleTap}>
          <MediaDisplay submission={submission} />

          {/* Review flash animation */}
          {showReviewFlash && (
            <div className="rfc-review-flash">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#6E7A5A" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="20,6 9,17 4,12" />
              </svg>
            </div>
          )}
        </div>

        {/* ═══ Action row ═══ */}
        <div className="rfc-actions">
          <button
            className={`rfc-action-btn ${submission.isStarred ? 'rfc-action--active' : ''}`}
            onClick={() => onToggleStar?.(submission.id)}
            aria-label={submission.isStarred ? 'Unstar' : 'Star'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={submission.isStarred ? '#C2AC8E' : 'none'} stroke={submission.isStarred ? '#C2AC8E' : 'currentColor'} strokeWidth="2" strokeLinecap="round">
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
          </button>

          <button
            className={`rfc-action-btn ${submission.isReviewed ? 'rfc-action--reviewed' : ''}`}
            onClick={() => onToggleReview?.(submission.id)}
            aria-label={submission.isReviewed ? 'Mark unreviewed' : 'Mark reviewed'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={submission.isReviewed ? '#6E7A5A' : 'currentColor'} strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
            {submission.isReviewed && <span className="rfc-reviewed-label">Reviewed</span>}
          </button>

          <button
            className={`rfc-action-btn rfc-action-comment ${hasUnread ? 'rfc-action--unread' : ''}`}
            onClick={() => setShowThread(prev => !prev)}
            aria-label="Comments"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {hasUnread && <span className="rfc-unread-dot" />}
          </button>
        </div>

        {/* ═══ Notes ═══ */}
        {submission.notes && !isText && (
          <div className="rfc-notes">{submission.notes}</div>
        )}

        {/* ═══ Tags (hidden for audio) ═══ */}
        {submission.tags && submission.tags.length > 0 && submission.mediaType !== 'audio' && (
          <div className="rfc-tags">
            {submission.tags.map((tag, i) => (
              <span key={i} className="rfc-tag">{tag}</span>
            ))}
          </div>
        )}

        {/* ═══ Inline thread ═══ */}
        {showThread && (
          <div className="rfc-thread">
            <ConversationThread
              submissionId={submission.id}
              organizationId={threadContext.organizationId}
              projectId={submission.projectId}
              participantId={submission.participantId}
              currentUserUid={threadContext.currentUserUid}
              currentUserName={threadContext.currentUserName}
              currentUserRole="researcher"
              taskName={taskName}
            />
          </div>
        )}
      </div>
    </div>
  );
};
